import type { McpTool } from "@p0security/shared/types/agentic/mcp";

// The tools/list output of p0security/mcp-dynamodb (agentic-gateway
// docker/mcp-dynamodb), without each input schema's $schema. The gateway
// serves this list as the server's only tool list, so keep it the same as
// the pinned image's.
export const dynamoDbMcpTools: McpTool[] = [
  {
    name: "describe_table",
    description:
      "Get the table's key schema, indexes, item count, size and time-to-live setting",
    annotations: {
      readOnlyHint: true,
      openWorldHint: true,
    },
    inputSchema: {
      type: "object",
      properties: {
        table_name: {
          type: "string",
          pattern: "^[a-zA-Z0-9_.-]{3,255}$",
          description: "The table name. P0 grants access to one table.",
        },
      },
      required: ["table_name"],
    },
  },
  {
    name: "get_item",
    description: "Read one item by its primary key",
    annotations: {
      readOnlyHint: true,
      openWorldHint: true,
    },
    inputSchema: {
      type: "object",
      properties: {
        table_name: {
          type: "string",
          pattern: "^[a-zA-Z0-9_.-]{3,255}$",
          description: "The table name. P0 grants access to one table.",
        },
        key: {
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {},
          description:
            'The primary key as plain JSON, e.g. {"pk": "user#1", "sk": 3}',
        },
        projection: {
          description: "The attributes to return, e.g. pk, #s",
          type: "string",
        },
        names: {
          description:
            'Expression attribute names, for reserved words, e.g. {"#s": "status"}',
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {
            type: "string",
          },
        },
        consistent_read: {
          description:
            "Read the latest write instead of an eventually consistent value",
          type: "boolean",
        },
      },
      required: ["table_name", "key"],
    },
  },
  {
    name: "batch_get_item",
    description:
      "Read up to 100 items by primary key. Read unprocessed_keys again with a later call.",
    annotations: {
      readOnlyHint: true,
      openWorldHint: true,
    },
    inputSchema: {
      type: "object",
      properties: {
        table_name: {
          type: "string",
          pattern: "^[a-zA-Z0-9_.-]{3,255}$",
          description: "The table name. P0 grants access to one table.",
        },
        keys: {
          minItems: 1,
          maxItems: 100,
          type: "array",
          items: {
            type: "object",
            propertyNames: {
              type: "string",
            },
            additionalProperties: {},
            description:
              'The primary key as plain JSON, e.g. {"pk": "user#1", "sk": 3}',
          },
          description: "Up to 100 primary keys",
        },
        projection: {
          description: "The attributes to return, e.g. pk, #s",
          type: "string",
        },
        names: {
          description:
            'Expression attribute names, for reserved words, e.g. {"#s": "status"}',
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {
            type: "string",
          },
        },
      },
      required: ["table_name", "keys"],
    },
  },
  {
    name: "query",
    description:
      "Read the items with one partition key value, in sort key order, a page at a time",
    annotations: {
      readOnlyHint: true,
      openWorldHint: true,
    },
    inputSchema: {
      type: "object",
      properties: {
        table_name: {
          type: "string",
          pattern: "^[a-zA-Z0-9_.-]{3,255}$",
          description: "The table name. P0 grants access to one table.",
        },
        key_condition: {
          type: "string",
          description:
            "The key condition expression, e.g. pk = :pk AND begins_with(sk, :p)",
        },
        values: {
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {},
          description:
            'Expression attribute values as plain JSON, e.g. {":pk": "user#1"}',
        },
        names: {
          description:
            'Expression attribute names, for reserved words, e.g. {"#s": "status"}',
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {
            type: "string",
          },
        },
        index_name: {
          description: "A global or local secondary index of the table",
          type: "string",
          pattern: "^[a-zA-Z0-9_.-]{3,255}$",
        },
        filter: {
          description:
            "A filter expression applied after the read; it does not lower the read cost",
          type: "string",
        },
        projection: {
          description: "The attributes to return, e.g. pk, #s",
          type: "string",
        },
        limit: {
          description: "The most items to read in this page",
          type: "integer",
          exclusiveMinimum: 0,
          maximum: 9007199254740991,
        },
        start_key: {
          description: "next_start_key from the previous page",
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {},
        },
      },
      required: ["table_name", "key_condition", "values"],
    },
  },
  {
    name: "scan",
    description:
      "Read every item of the table or index, a page at a time. Use query when you know the partition key.",
    annotations: {
      readOnlyHint: true,
      openWorldHint: true,
    },
    inputSchema: {
      type: "object",
      properties: {
        table_name: {
          type: "string",
          pattern: "^[a-zA-Z0-9_.-]{3,255}$",
          description: "The table name. P0 grants access to one table.",
        },
        index_name: {
          description: "A global or local secondary index of the table",
          type: "string",
          pattern: "^[a-zA-Z0-9_.-]{3,255}$",
        },
        filter: {
          description:
            "A filter expression applied after the read; it does not lower the read cost",
          type: "string",
        },
        values: {
          description:
            'Expression attribute values as plain JSON, e.g. {":s": "open"}',
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {},
        },
        names: {
          description:
            'Expression attribute names, for reserved words, e.g. {"#s": "status"}',
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {
            type: "string",
          },
        },
        projection: {
          description: "The attributes to return, e.g. pk, #s",
          type: "string",
        },
        limit: {
          description: "The most items to read in this page",
          type: "integer",
          exclusiveMinimum: 0,
          maximum: 9007199254740991,
        },
        start_key: {
          description: "next_start_key from the previous page",
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {},
        },
      },
      required: ["table_name"],
    },
  },
  {
    name: "put_item",
    description: "Write a whole item. It replaces an item with the same key.",
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: true,
      openWorldHint: true,
    },
    inputSchema: {
      type: "object",
      properties: {
        table_name: {
          type: "string",
          pattern: "^[a-zA-Z0-9_.-]{3,255}$",
          description: "The table name. P0 grants access to one table.",
        },
        item: {
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {},
          description: "The whole item as plain JSON",
        },
        condition: {
          description:
            "A condition expression; the write fails when it is false, e.g. attribute_not_exists(pk)",
          type: "string",
        },
        values: {
          description:
            'Expression attribute values as plain JSON, e.g. {":s": "open"}',
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {},
        },
        names: {
          description:
            'Expression attribute names, for reserved words, e.g. {"#s": "status"}',
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {
            type: "string",
          },
        },
      },
      required: ["table_name", "item"],
    },
  },
  {
    name: "update_item",
    description:
      "Change attributes of one item, or make it when it does not exist. Returns the item after the change.",
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: false,
      openWorldHint: true,
    },
    inputSchema: {
      type: "object",
      properties: {
        table_name: {
          type: "string",
          pattern: "^[a-zA-Z0-9_.-]{3,255}$",
          description: "The table name. P0 grants access to one table.",
        },
        key: {
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {},
          description:
            'The primary key as plain JSON, e.g. {"pk": "user#1", "sk": 3}',
        },
        update: {
          type: "string",
          description: "The update expression, e.g. SET #s = :s REMOVE note",
        },
        values: {
          description:
            'Expression attribute values as plain JSON, e.g. {":s": "open"}',
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {},
        },
        names: {
          description:
            'Expression attribute names, for reserved words, e.g. {"#s": "status"}',
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {
            type: "string",
          },
        },
        condition: {
          description:
            "A condition expression; the write fails when it is false, e.g. attribute_not_exists(pk)",
          type: "string",
        },
      },
      required: ["table_name", "key", "update"],
    },
  },
  {
    name: "delete_item",
    description: "Delete one item by its primary key",
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: true,
      openWorldHint: true,
    },
    inputSchema: {
      type: "object",
      properties: {
        table_name: {
          type: "string",
          pattern: "^[a-zA-Z0-9_.-]{3,255}$",
          description: "The table name. P0 grants access to one table.",
        },
        key: {
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {},
          description:
            'The primary key as plain JSON, e.g. {"pk": "user#1", "sk": 3}',
        },
        condition: {
          description:
            "A condition expression; the write fails when it is false, e.g. attribute_not_exists(pk)",
          type: "string",
        },
        values: {
          description:
            'Expression attribute values as plain JSON, e.g. {":s": "open"}',
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {},
        },
        names: {
          description:
            'Expression attribute names, for reserved words, e.g. {"#s": "status"}',
          type: "object",
          propertyNames: {
            type: "string",
          },
          additionalProperties: {
            type: "string",
          },
        },
      },
      required: ["table_name", "key"],
    },
  },
  {
    name: "batch_write_item",
    description:
      "Put and delete up to 25 items in all, with no conditions. Send unprocessed_puts and unprocessed_deletes again with a later call.",
    annotations: {
      readOnlyHint: false,
      destructiveHint: true,
      idempotentHint: true,
      openWorldHint: true,
    },
    inputSchema: {
      type: "object",
      properties: {
        table_name: {
          type: "string",
          pattern: "^[a-zA-Z0-9_.-]{3,255}$",
          description: "The table name. P0 grants access to one table.",
        },
        puts: {
          description: "Items to put",
          type: "array",
          items: {
            type: "object",
            propertyNames: {
              type: "string",
            },
            additionalProperties: {},
            description: "The whole item as plain JSON",
          },
        },
        deletes: {
          description: "Primary keys to delete",
          type: "array",
          items: {
            type: "object",
            propertyNames: {
              type: "string",
            },
            additionalProperties: {},
            description:
              'The primary key as plain JSON, e.g. {"pk": "user#1", "sk": 3}',
          },
        },
      },
      required: ["table_name"],
    },
  },
];
